import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import Layout from '../components/layout'
import Page from '../components/Page'

const Index = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata.title
  const homePage = data.contentfulPage
  const sortableData = data.allContentfulSortableComponents.edges
  const alertBarData = data.allContentfulAlertBar.edges[0].node
  const navBarData = data.contentfulNavigationBar
  const footerData = data.contentfulFooterV2
  const siteLanguages = data.site.siteMetadata.languages
  const latestBlogPost = data.latestPost.edges[0].node
  const pageAlert = data.contentfulPage.navAlert
  return (
    <Layout
      latestBlogPost={latestBlogPost}
      location={location}
      alertBarData={alertBarData}
      navBarData={navBarData}
      footerData={footerData}
      siteLanguages={siteLanguages}
      pageAlert={pageAlert}
    >
      <div style={{ background: '#fff' }}>
        <Helmet title={siteTitle} />
        <Page data={homePage} sortableData={sortableData} />
      </div>
    </Layout>
  )
}

export default Index

export const pageQuery = graphql`
  fragment ContentfulPageQuery on ContentfulPage {
    title
    slug
    node_locale
    seoImage {
      file {
        fileName
        url
        details {
          image {
            height
            width
          }
        }
      }
      gatsbyImageData(layout: FULL_WIDTH)
      description
    }
    seoTitle
    canonicalUrl
    description
    ogLocale
    ogType
    ogTitle
    ogDescription
    ogImage {
      file {
        fileName
        url
        details {
          image {
            height
            width
          }
        }
      }
      gatsbyImageData(layout: FULL_WIDTH)
      description
    }
    blocks {
      page {
        slug
      }
      title
      anchorID
      blockVisibility
      backgroundColor
      imageTextPosition
      subtitle {
        subtitle
        childMarkdownRemark {
          html
        }
      }
      hideTitle
      images {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        sevenColumnImage: gatsbyImageData(layout: FULL_WIDTH, width: 751)
        iphero: gatsbyImageData(layout: FULL_WIDTH, width: 1900)
        quote: gatsbyImageData(layout: CONSTRAINED, width: 84, height: 84)
        gatsbyImageData(layout: FULL_WIDTH)
        description
      }
      mobileOnlyImages {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        gatsbyImageData(layout: FULL_WIDTH)
        description
      }
      order
      type
      ctaText
      ctaUrl
      anchorID
      video_ref {
        title
        videoId
        videoPlaylistId
        videoUrl
        backgroundImage {
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          gatsbyImageData(layout: FULL_WIDTH)
          description
        }
        autoplay
      }
      contactForm_ref {
        portalId
        formId
        loadingText
        webinarKey
        marketoId
        redirect
      }
      spacer_ref {
        backgroundColor
        includeHorizontalRule
        useOusterGradient
        horizontalRuleColor
        horizontalRuleHeight
        height
        mobileHeight
        displayOnMobile
      }
      sortableComponentType
      sortableComponentSortableFields
      sortableComponentNoResultsText
      sortableComponent {
        pageblock {
          page {
            slug
            title
          }
        }
        order
        title
        description {
          description
          childMarkdownRemark {
            html
          }
        }
        type
        website
        logo {
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          gatsbyImageData(layout: FULL_WIDTH)
          description
        }
        hqLocation
        regionsServed
        servicesNeeded
        industry
        countriesServed
      }
      components {
        id
        title
        subtitle {
          subtitle
          childMarkdownRemark {
            html
          }
        }
        superHeader
        order
        image {
          description
          file {
            url
            fileName
            details {
              image {
                height
                width
              }
            }
          }
          valueIcon: gatsbyImageData(layout: CONSTRAINED, width: 56, height: 56)
          cardsWithIconsIcon: gatsbyImageData(
            layout: CONSTRAINED
            width: 35
            height: 35
          )
          scrollingCardsImage: gatsbyImageData(
            layout: CONSTRAINED
            width: 304
            height: 260
          )
          scrollingCardsMobileImage: gatsbyImageData(
            layout: CONSTRAINED
            width: 268
            height: 229
          )
          fourAcrossSquaresImage: gatsbyImageData(
            layout: FULL_WIDTH
            width: 380
            height: 380
          )
          gatsbyImageData(layout: FULL_WIDTH)
        }
        secondaryImages {
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          description
          gatsbyImageData(layout: FULL_WIDTH)
        }
        link
        sentenceList
        ctaText
        ctaUrl
        ctaDefaultDisplay
        type
        focalLength
        fov
        multiCtatext {
          id
          headline
          ctaBodyText {
            ctaBodyText
          }
          ctaUrl
          ctaUrlText
          image {
            description
            file {
              url
              fileName
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
          }
          ctaUrlInternal
          ctaDefaultDisplay
        }
        color
        imageTextPosition
      }
      isDarkTheme
      backgroundImageFillMode
      alternateDisplayMode
      quoteAuthor
      quoteAuthorTitle
    }
    navAlert {
      linkUrl
      mobileText
      type
      text
      ctaText
      hideAlert
    }
  }

  fragment ContentfulNavQuery on ContentfulNavigationBar {
    title
    superNavLinks {
      label
      order
      defaultUrl
      hide
    }
    links {
      label
      hoverGradient
      inverted
      order
      defaultUrl
      hide
      groupHeader
      groupHeaderUrl
      groupHeaderImage {
        file {
          fileName
          url
          details {
            image {
              height
              width
            }
          }
        }
        description
        gatsbyImageData(layout: CONSTRAINED, width: 30, height: 30)
      }
      groupId
      childLinks {
        ctaText
        url
        label
        subtitle
        type
        order
        newProductLink
        hide
        image {
          gatsbyImageData(layout: FULL_WIDTH)
          file {
            fileName
            url
            details {
              image {
                height
                width
              }
            }
          }
          description
        }
      }
    }
  }

  fragment ContentfulFooterQuery on ContentfulFooterV2 {
    logo {
      gatsbyImageData(layout: CONSTRAINED, width: 64)
      description
    }
    logoAttr {
      width
      height
      maxWidth
      maxHeight
    }
    nyseLink
    nyseLogo {
      gatsbyImageData(layout: CONSTRAINED, width: 75)
      file {
        url
        details {
          image {
            height
            width
          }
        }
      }
      description
    }
    copyrightText
    privacyPolicyText
    privacyPolicyUrl
    siteTermsText
    siteTermsUrl
    basedInText
    sections {
      url
      label
      sublinks {
        label
        url
      }
    }
    mobileButtons {
      label
      url
    }
  }

  {
    site {
      siteMetadata {
        title
        languages {
          defaultLangKey
          langs
        }
      }
    }

    latestPost: allWpPost(
      sort: { fields: date, order: DESC }
      limit: 1
      filter: { locale: { locale: { eq: "en_US" } }, status: { eq: "publish" } }
    ) {
      edges {
        node {
          ...LatestPost
        }
      }
    }

    allContentfulAlertBar(
      filter: { node_locale: { eq: "en-US" }, type: { eq: "Normal" } }
    ) {
      edges {
        node {
          type
          text
          mobileText
          linkUrl
          id
        }
      }
    }

    contentfulNavigationBar(node_locale: { eq: "en-US" }) {
      ...ContentfulNavQuery
    }

    contentfulPage(slug: { eq: "/" }, node_locale: { eq: "en-US" }) {
      ...ContentfulPageQuery
    }

    contentfulFooterV2(node_locale: { eq: "en-US" }) {
      ...ContentfulFooterQuery
    }

    allContentfulSortableComponents(filter: { node_locale: { eq: "en-US" } }) {
      edges {
        node {
          id
          contentful_id
          node_locale
          type
          title
          link
          order
          pageblock {
            page {
              slug
              title
            }
          }
          title
          description {
            description
            childMarkdownRemark {
              html
            }
          }
          type
          website
          logo {
            file {
              fileName
              url
              details {
                image {
                  height
                  width
                }
              }
            }
            gatsbyImageData(layout: FULL_WIDTH)
            description
          }
        }
      }
    }

    sortableComponentsContent(sys: { id: { eq: "allData" } }) {
      data {
        dataString
      }
    }

    allSortableComponents {
      edges {
        node {
          name
          id
          items {
            type
            validations {
              in
            }
          }
        }
      }
    }
  }
`
